import { Link } from "react-router-dom";
import { Search, TrendingUp, ExternalLink, MapPin, Stethoscope } from "lucide-react";
import { CITIES, SERVICES } from "@/lib/data";

const PRIMARY_KEYWORDS = [
  "Non Emergency Medical Transportation Virginia",
  "NEMT Virginia DC Maryland",
  "Medical Transportation Washington DC",
  "Wheelchair Transportation Maryland",
  "Dialysis Transportation Virginia DC",
  "Hospital Discharge Transportation DC",
  "Senior Transportation Services Maryland",
  "Wheelchair Accessible Transportation DMV",
  "Medical Transportation Near Me",
  "Private Medical Transportation Virginia DC MD",
];

const SECONDARY_KEYWORDS = [
  "Medical Ride Services DMV",
  "Accessible Transportation DC",
  "Patient Transportation Maryland",
  "Healthcare Transportation Virginia",
  "Doctor Appointment Transportation DC",
  "Long Distance Medical Transportation",
  "Recurring Medical Transportation",
  "ADA Compliant Transportation DMV",
  "Stretcher Transportation Maryland",
  "Non-Ambulatory Transport VA DC MD",
];

// Trusted external resources that benefit user (genuine backlinks for healthcare audience)
const RESOURCES = [
  { label: "Virginia Department of Health", url: "https://www.vdh.virginia.gov/", desc: "Statewide public health resources & services" },
  { label: "Medicare Non-Emergency Transport", url: "https://www.medicare.gov/", desc: "Coverage info for medical transportation" },
  { label: "ADA Accessibility Guidelines", url: "https://www.ada.gov/", desc: "Official ADA standards we follow" },
  { label: "Virginia Association for Home Care", url: "https://www.vahc.org/", desc: "Home health & senior care partners" },
  { label: "National Kidney Foundation", url: "https://www.kidney.org/", desc: "Dialysis patient resources" },
  { label: "Eldercare Locator (Admin for Community Living)", url: "https://eldercare.acl.gov/", desc: "Senior services nationwide" },
];

export default function SeoHub() {
  return (
    <section data-testid="seo-hub" id="resources" className="section-pad bg-white">
      <div className="container-pad mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <span className="eyebrow">
              <span className="w-8 h-px bg-teal-500" />
              Resources & Search
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
              Helpful resources & <strong className="font-bold text-teal-600">popular searches.</strong>
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-base md:text-lg text-navy/65 leading-relaxed font-light">
              Whether you searched for "medical transportation near me" or you're researching senior care options
              in Virginia, here are the resources and topics families ask about most.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Primary keywords */}
          <div className="lg:col-span-7 card-soft p-7 md:p-9">
            <div className="flex items-center gap-2 text-teal-600 mb-5">
              <Search className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-widest">Top Searches We Help With</span>
            </div>
            <h3 className="font-heading text-2xl font-semibold text-navy">Primary keywords</h3>
            <p className="text-navy/65 text-sm mt-2">If you searched any of these, you're in the right place.</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {PRIMARY_KEYWORDS.map((k) => (
                <span key={k} className="px-3.5 py-1.5 rounded-full bg-teal-500/10 text-teal-700 text-xs font-medium border border-teal-500/20">
                  {k}
                </span>
              ))}
            </div>
            <div className="mt-7 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-2 text-navy/60 mb-3">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-widest">Related searches</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {SECONDARY_KEYWORDS.map((k) => (
                  <span key={k} className="px-3 py-1 rounded-full bg-sand border border-sand-200 text-navy/70 text-xs">
                    {k}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Internal SEO links: cities */}
          <div className="lg:col-span-5 grid grid-cols-1 gap-6">
            <div className="card-soft p-7 md:p-9">
              <div className="flex items-center gap-2 text-teal-600 mb-5">
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-widest">Service Area Pages</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-navy mb-4">Virginia city pages</h3>
              <div className="grid grid-cols-2 gap-2">
                {CITIES.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/service-area/${c.slug}`}
                    className="text-sm text-navy/75 hover:text-teal-600 hover:translate-x-0.5 transition flex items-center gap-1.5"
                  >
                    <span className="w-1 h-1 rounded-full bg-teal-500" />
                    {c.name} NEMT
                  </Link>
                ))}
              </div>
            </div>

            <div className="card-soft p-7 md:p-9">
              <div className="flex items-center gap-2 text-teal-600 mb-5">
                <Stethoscope className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-widest">Service Pages</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-navy mb-4">Browse our services</h3>
              <div className="grid grid-cols-1 gap-1.5">
                {SERVICES.slice(0, 6).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="text-sm text-navy/75 hover:text-teal-600 hover:translate-x-0.5 transition flex items-center gap-1.5"
                  >
                    <span className="w-1 h-1 rounded-full bg-teal-500" />
                    {s.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* External resources / backlinks */}
        <div className="mt-8 card-soft p-7 md:p-9">
          <div className="flex items-center gap-2 text-teal-600 mb-5">
            <ExternalLink className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-widest">Trusted Resources for Families</span>
          </div>
          <h3 className="font-heading text-2xl font-semibold text-navy">Helpful healthcare & senior care links</h3>
          <p className="text-navy/65 text-sm mt-2 max-w-2xl">
            We're proud to support a wider ecosystem of care. These trusted organizations offer additional information
            for patients and caregivers across Virginia.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
            {RESOURCES.map((r) => (
              <a
                key={r.label}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group p-4 rounded-2xl border border-gray-100 hover:border-teal-300 hover:bg-sand transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading font-semibold text-navy text-sm leading-snug">{r.label}</div>
                    <div className="text-xs text-navy/60 mt-1.5 leading-relaxed">{r.desc}</div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-teal-500 mt-1 shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
